import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Shield, Heart, Zap, Footprints, Swords,
  User, Crown, Users, ScrollText, BookOpen,
  Save, LogOut, Loader2, Clock, FileText, Dices,
  Castle, Map, Flag, History, Gamepad2
} from "lucide-react";
import DiceRoller from "@/components/DiceRoller";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlayerPortal = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editedCharacter, setEditedCharacter] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState(null);
  const [sharedSessions, setSharedSessions] = useState([]);
  const [sharedLocations, setSharedLocations] = useState([]);
  const [sharedMaps, setSharedMaps] = useState([]);
  const [sharedFactions, setSharedFactions] = useState([]);
  const [sharedTimeline, setSharedTimeline] = useState([]);

  const guestToken = localStorage.getItem(`guest_${campaignId}`);

  const guestHeaders = { "X-Guest-Token": guestToken || "" };

  useEffect(() => {
    if (!guestToken) {
      setError("No active session. Please use your invite link to rejoin.");
      setLoading(false);
      return;
    }
    fetchPortal();
  }, [campaignId, guestToken]);

  const fetchPortal = async () => {
    try {
      const res = await axios.get(`${API}/guest/portal/${campaignId}`, { headers: guestHeaders });
      setPortalData(res.data);
      if (res.data.character) {
        setEditedCharacter({ ...res.data.character });
      }
      // Fetch shared sessions
      try {
        const sessRes = await axios.get(`${API}/guest/portal/${campaignId}/shared-sessions`, { headers: guestHeaders });
        setSharedSessions(sessRes.data);
      } catch { /* no shared sessions */ }
      // Fetch shared world content
      try {
        const [locRes, mapRes, facRes, tlRes] = await Promise.allSettled([
          axios.get(`${API}/guest/portal/${campaignId}/locations`, { headers: guestHeaders }),
          axios.get(`${API}/guest/portal/${campaignId}/maps`, { headers: guestHeaders }),
          axios.get(`${API}/guest/portal/${campaignId}/factions`, { headers: guestHeaders }),
          axios.get(`${API}/guest/portal/${campaignId}/timeline`, { headers: guestHeaders }),
        ]);
        if (locRes.status === "fulfilled") setSharedLocations(locRes.value.data);
        if (mapRes.status === "fulfilled") setSharedMaps(mapRes.value.data);
        if (facRes.status === "fulfilled") setSharedFactions(facRes.value.data);
        if (tlRes.status === "fulfilled") setSharedTimeline(tlRes.value.data);
      } catch { /* ignore */ }
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(`guest_${campaignId}`);
        setError("Session expired. Please use your invite link to rejoin.");
      } else {
        setError("Failed to load portal data.");
      }
    } finally {
      setLoading(false);
    }
  };



  const handleCharacterChange = (field, value) => {
    setEditedCharacter((prev) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSheetChange = (field, value) => {
    setEditedCharacter((prev) => ({
      ...prev,
      character_sheet: { ...(prev.character_sheet || {}), [field]: value },
    }));
    setHasChanges(true);
  };

  const saveCharacter = async () => {
    if (!editedCharacter) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/guest/portal/${campaignId}/character`, editedCharacter, {
        headers: { ...guestHeaders, "Content-Type": "application/json" },
      });
      setEditedCharacter({ ...res.data });
      setPortalData((prev) => ({ ...prev, character: res.data }));
      setHasChanges(false);
      toast.success("Character saved!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save character");
    } finally {
      setSaving(false);
    }
  };

  const handleLeave = () => {
    localStorage.removeItem(`guest_${campaignId}`);
    toast.success("You've left the campaign");
    navigate("/");
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-card border-border">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-destructive" />
            </div>
            <h2 className="text-xl font-serif text-foreground mb-2">Session Error</h2>
            <p className="text-muted-foreground mb-6" data-testid="portal-error-message">{error}</p>
            <Button onClick={() => navigate("/")} className="bg-primary text-primary-foreground" data-testid="portal-go-home-button">
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const member = portalData?.member;
  const campaign = portalData?.campaign;
  const character = editedCharacter;
  const party = portalData?.party || [];
  const sheet = character?.character_sheet || {};

  return (
    <div className="min-h-screen bg-background" data-testid="player-portal-page">
      {/* Header */}
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ScrollText className="w-6 h-6 text-primary" />
            <div>
              <h1 className="text-lg font-serif text-foreground" data-testid="portal-campaign-name">{campaign?.name}</h1>
              <p className="text-xs text-muted-foreground">
                Playing as <span className="text-primary font-medium">{member?.character_name}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(`/campaign/${campaignId}/wiki`)} className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground" data-testid="open-wiki-button">
              <BookOpen className="w-4 h-4 mr-1" /> Open Wiki
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate(`/campaign/${campaignId}/player-vtt`)} className="border-primary/30 text-primary hover:bg-primary hover:text-primary-foreground" data-testid="join-vtt-button">
              <Gamepad2 className="w-4 h-4 mr-1" /> Join Tabletop
            </Button>
            {hasChanges && (
              <Button
                onClick={saveCharacter}
                disabled={saving}
                size="sm"
                className="bg-primary text-primary-foreground"
                data-testid="save-character-button"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                Save
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={handleLeave} className="text-muted-foreground" data-testid="leave-campaign-button">
              <LogOut className="w-4 h-4 mr-1" /> Leave
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* No character linked yet */}
        {!character && (
          <Card className="bg-card border-border" data-testid="no-character-card">
            <CardContent className="py-12 text-center">
              <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Swords className="w-10 h-10 text-primary" />
              </div>
              <h2 className="text-xl font-serif text-foreground mb-2">Awaiting Character Assignment</h2>
              <p className="text-muted-foreground max-w-sm mx-auto">
                Your DM hasn't linked a character sheet to you yet.
                Once they do, you'll be able to view and edit your character here.
              </p>
              <Badge variant="outline" className="mt-4 border-primary/30 text-primary">
                {member?.player_name} — {member?.character_name}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Character Sheet */}
        {character && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column — Core Info */}
            <div className="lg:col-span-2 space-y-6">
              {/* Identity */}
              <Card className="bg-card border-border" data-testid="character-identity-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <User className="w-5 h-5 text-primary" />
                    Character Identity
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-1">Name</label>
                      <Input
                        value={character.name || ""}
                        onChange={(e) => handleCharacterChange("name", e.target.value)}
                        className="bg-input border-border"
                        data-testid="char-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-1">Race</label>
                      <Input
                        value={character.race || ""}
                        onChange={(e) => handleCharacterChange("race", e.target.value)}
                        className="bg-input border-border"
                        data-testid="char-race-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-1">Class</label>
                      <Input
                        value={character.character_class || ""}
                        onChange={(e) => handleCharacterChange("character_class", e.target.value)}
                        className="bg-input border-border"
                        data-testid="char-class-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-foreground block mb-1">Level</label>
                      <Input
                        type="number"
                        value={character.level || 1}
                        onChange={(e) => handleCharacterChange("level", parseInt(e.target.value) || 1)}
                        className="bg-input border-border"
                        data-testid="char-level-input"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Combat Stats */}
              <Card className="bg-card border-border" data-testid="character-stats-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl flex items-center gap-2">
                    <Swords className="w-5 h-5 text-primary" />
                    Combat Stats
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatBox icon={<Shield className="w-4 h-4" />} label="Armor Class"
                      value={character.armor_class || ""}
                      onChange={(v) => handleCharacterChange("armor_class", v)}
                      testId="char-ac-input"
                    />
                    <StatBox icon={<Heart className="w-4 h-4" />} label="Hit Points"
                      value={character.hit_points || ""}
                      onChange={(v) => handleCharacterChange("hit_points", v)}
                      testId="char-hp-input"
                    />
                    <StatBox icon={<Footprints className="w-4 h-4" />} label="Speed"
                      value={character.speed || ""}
                      onChange={(v) => handleCharacterChange("speed", v)}
                      testId="char-speed-input"
                    />
                    <StatBox icon={<Zap className="w-4 h-4" />} label="Initiative"
                      value={character.initiative || ""}
                      onChange={(v) => handleCharacterChange("initiative", v)}
                      testId="char-init-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Ability Scores (from character_sheet) */}
              <Card className="bg-card border-border" data-testid="character-abilities-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl">Ability Scores</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {["STR", "DEX", "CON", "INT", "WIS", "CHA"].map((stat) => (
                      <div key={stat} className="text-center">
                        <label className="text-xs font-bold text-muted-foreground block mb-1">{stat}</label>
                        <Input
                          className="text-center bg-input border-border font-mono"
                          value={sheet[stat.toLowerCase()] || "10"}
                          onChange={(e) => handleSheetChange(stat.toLowerCase(), e.target.value)}
                          data-testid={`char-${stat.toLowerCase()}-input`}
                        />
                        <span className="text-xs text-muted-foreground">
                          {(() => { const v = parseInt(sheet[stat.toLowerCase()] || "10"); return isNaN(v) ? "+0" : (Math.floor((v - 10) / 2) >= 0 ? "+" : "") + Math.floor((v - 10) / 2); })()}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Description & Backstory */}
              <Card className="bg-card border-border" data-testid="character-backstory-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl">Description & Backstory</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">Description</label>
                    <Textarea
                      value={character.description || ""}
                      onChange={(e) => handleCharacterChange("description", e.target.value)}
                      placeholder="What does your character look like?"
                      rows={3}
                      className="bg-input border-border"
                      data-testid="char-description-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-foreground block mb-1">Backstory</label>
                    <Textarea
                      value={character.backstory || ""}
                      onChange={(e) => handleCharacterChange("backstory", e.target.value)}
                      placeholder="Your character's backstory..."
                      rows={5}
                      className="bg-input border-border"
                      data-testid="char-backstory-input"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Notes */}
              <Card className="bg-card border-border" data-testid="character-notes-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl">Player Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={sheet.notes || ""}
                    onChange={(e) => handleSheetChange("notes", e.target.value)}
                    placeholder="Session notes, reminders, quest info..."
                    rows={6}
                    className="bg-input border-border"
                    data-testid="char-notes-input"
                  />
                </CardContent>
              </Card>

              {/* Inventory */}
              <Card className="bg-card border-border" data-testid="character-inventory-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-xl">Inventory</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={sheet.inventory || ""}
                    onChange={(e) => handleSheetChange("inventory", e.target.value)}
                    placeholder="List your items, gold, equipment..."
                    rows={5}
                    className="bg-input border-border"
                    data-testid="char-inventory-input"
                  />
                </CardContent>
              </Card>
            </div>

            {/* Right Column — Party & Info */}
            <div className="space-y-6">
              {/* Player Info Badge */}
              <Card className="bg-card border-border" data-testid="player-info-card">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="h-12 w-12">
                      <AvatarFallback className="bg-primary/10 text-primary font-bold text-lg">
                        {(character.name || "?")[0].toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h3 className="font-serif text-lg text-foreground">{character.name}</h3>
                      <p className="text-sm text-muted-foreground">
                        {character.race} {character.character_class} {character.level ? `(Lv. ${character.level})` : ""}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Party Members */}
              <Card className="bg-card border-border" data-testid="party-list-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Users className="w-4 h-4 text-primary" />
                    The Party
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {party.map((p, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2 rounded-md ${p.is_self ? "bg-primary/5 border border-primary/20" : ""}`}>
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className={`text-xs ${p.role === "dm" ? "bg-amber-500/20 text-amber-500" : "bg-primary/10 text-primary"}`}>
                          {p.role === "dm" ? <Crown className="w-3 h-3" /> : (p.character_name || p.name || "?")[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">
                          {p.role === "dm" ? p.name : (p.character_name || p.name)}
                          {p.is_self && <span className="text-primary text-xs ml-1">(You)</span>}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {p.role === "dm" ? "Dungeon Master" : p.name}
                        </p>
                      </div>
                      {p.role === "dm" && (
                        <Badge variant="outline" className="border-amber-500/30 text-amber-500 text-xs shrink-0">DM</Badge>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
              {/* Shared Session Recaps */}
              {sharedSessions.length > 0 && (
                <Card className="bg-card border-border" data-testid="shared-sessions-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="font-serif text-lg flex items-center gap-2">
                      <Clock className="w-4 h-4 text-primary" />
                      Session Recaps
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {sharedSessions.map((sess) => (
                      <div key={sess.id} className="border border-border rounded-md p-3" data-testid={`shared-session-${sess.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-medium text-foreground">{sess.title}</h4>
                          <span className="text-[10px] text-muted-foreground">
                            {sess.started_at ? new Date(sess.started_at).toLocaleDateString([], { month: "short", day: "numeric" }) : ""}
                          </span>
                        </div>
                        {sess.entries && sess.entries.length > 0 ? (
                          <div className="space-y-1 max-h-40 overflow-y-auto">
                            {sess.entries.map((entry, idx) => (
                              <div key={entry.id || idx} className="flex items-start gap-2 text-xs">
                                <span className="text-muted-foreground mt-0.5 shrink-0">
                                  {entry.type === "dice_roll" ? <Dices className="w-3 h-3" /> : entry.type === "combat_event" ? <Swords className="w-3 h-3 text-red-400" /> : <FileText className="w-3 h-3" />}
                                </span>
                                <span className={`${entry.type === "combat_event" ? "text-red-400" : entry.type === "dice_roll" ? "text-primary" : "text-foreground/80"}`}>
                                  {entry.content}
                                </span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-muted-foreground italic">No entries.</p>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        )}

        {/* ==================== SHARED WORLD CONTENT ==================== */}
        {(sharedLocations.length > 0 || sharedMaps.length > 0 || sharedFactions.length > 0 || sharedTimeline.length > 0) && (
          <div className="mt-8 space-y-6" data-testid="shared-world-content">
            <h2 className="text-xl font-serif text-foreground border-b border-border pb-2">World Lore</h2>

            {/* Shared Locations */}
            {sharedLocations.length > 0 && (
              <Card className="bg-card border-border" data-testid="shared-locations-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Castle className="w-4 h-4 text-primary" />
                    Locations ({sharedLocations.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sharedLocations.map((loc) => (
                      <div key={loc.id} className="border border-border rounded-md p-3" data-testid={`shared-location-${loc.id}`}>
                        <h4 className="text-sm font-medium text-foreground">{loc.name}</h4>
                        <p className="text-xs text-muted-foreground capitalize">{loc.location_type || "Location"}</p>
                        {loc.description && (
                          <p className="text-xs text-foreground/70 mt-1 line-clamp-3">{typeof loc.description === "string" && loc.description.startsWith("<") ? loc.description.replace(/<[^>]+>/g, "").slice(0, 120) : (loc.description || "").slice(0, 120)}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shared Maps */}
            {sharedMaps.length > 0 && (
              <Card className="bg-card border-border" data-testid="shared-maps-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Map className="w-4 h-4 text-primary" />
                    Maps ({sharedMaps.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sharedMaps.map((m) => (
                      <div key={m.id} className="border border-border rounded-md p-3" data-testid={`shared-map-${m.id}`}>
                        <h4 className="text-sm font-medium text-foreground">{m.name}</h4>
                        {m.description && <p className="text-xs text-foreground/70 mt-1 line-clamp-2">{m.description}</p>}
                        {m.pins?.length > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-1">{m.pins.length} marked location{m.pins.length !== 1 ? "s" : ""}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shared Factions */}
            {sharedFactions.length > 0 && (
              <Card className="bg-card border-border" data-testid="shared-factions-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <Flag className="w-4 h-4 text-primary" />
                    Factions ({sharedFactions.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sharedFactions.map((f) => (
                      <div key={f.id} className="border border-border rounded-md p-3" data-testid={`shared-faction-${f.id}`}>
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-foreground">{f.name}</h4>
                          {f.alignment && <Badge variant="outline" className="text-[10px] border-border">{f.alignment}</Badge>}
                        </div>
                        {f.description && <p className="text-xs text-foreground/70 mt-1 line-clamp-3">{f.description}</p>}
                        {f.goals && <p className="text-[10px] text-muted-foreground mt-1 italic">Goals: {f.goals}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shared Timeline */}
            {sharedTimeline.length > 0 && (
              <Card className="bg-card border-border" data-testid="shared-timeline-card">
                <CardHeader className="pb-3">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <History className="w-4 h-4 text-primary" />
                    World History ({sharedTimeline.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {sharedTimeline.map((evt) => (
                      <div key={evt.id} className="border border-border rounded-md p-3" data-testid={`shared-event-${evt.id}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-medium text-foreground">{evt.title}</h4>
                          {evt.date_label && <span className="text-[10px] text-muted-foreground">{evt.date_label}</span>}
                        </div>
                        {evt.era && <Badge variant="outline" className="text-[10px] border-border mb-1">{evt.era}</Badge>}
                        {evt.description && (
                          <p className="text-xs text-foreground/70 mt-1 line-clamp-3">
                            {typeof evt.description === "string" && evt.description.startsWith("<") ? evt.description.replace(/<[^>]+>/g, "").slice(0, 200) : (evt.description || "").slice(0, 200)}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
      <DiceRoller />
    </div>
  );
};

const StatBox = ({ icon, label, value, onChange, testId }) => (
  <div className="text-center p-3 rounded-lg bg-muted/50 border border-border">
    <div className="flex items-center justify-center gap-1 text-muted-foreground mb-1">
      {icon}
      <span className="text-xs font-medium">{label}</span>
    </div>
    <Input
      className="text-center bg-input border-border text-lg font-mono"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
    />
  </div>
);

export default PlayerPortal;
